import type { FetchRequestError } from "@lib/errors";
import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type { SHARED_CONSTANTS_KNOWLEDGE_SOURCE } from "@lib/shared_constants";

export type KnowledgeSourceDefinitionStatus = "active" | "in_progress" | "marked_for_deletion" | "errored";

interface BaseKnowledgeSourceDefinitionFields {
  id: string;
  name: string;
  description: string;
  status: KnowledgeSourceDefinitionStatus;
  syncing_from_dashboard_trigger: boolean;
  created_at: string;
  updated_at: string;
  created_by_email: string;
}

export interface CatalogKnowledgeSourceDefinitionResultObject extends BaseKnowledgeSourceDefinitionFields {
  type: "catalog";
  catalog_id: string;
  last_synced_at: string | null;
}

export type KnowledgeSourceDefinitionIdResultObject = CatalogKnowledgeSourceDefinitionResultObject;

export interface KnowledgeSourceDefinitionIdResult {
  result: KnowledgeSourceDefinitionIdResultObject;
}

export interface DeleteKnowledgeSourceDefinitionIdProps {
  appGroupId: string;
  definitionId: string;
}

export interface RefreshKnowledgeSourceDefinitionIdProps {
  appGroupId: string;
  definitionId: string;
}

export interface UpdateKnowledgeSourceDefinitionIdProps {
  appGroupId: string;
  definitionId: string;
  description: string;
}

export type UpdateKnowledgeSourceDefinitionIdDescriptionRequiredError = FetchUserEncodedError<
  SHARED_CONSTANTS_KNOWLEDGE_SOURCE["ERROR_CODES"]["DESCRIPTION_REQUIRED"]
>;
export type UpdateKnowledgeSourceDefinitionIdDescriptionTooLongError = FetchUserEncodedError<
  SHARED_CONSTANTS_KNOWLEDGE_SOURCE["ERROR_CODES"]["DESCRIPTION_TOO_LONG"],
  { max: number }
>;

export type UpdateKnowledgeSourceDefinitionIdErrors =
  | UpdateKnowledgeSourceDefinitionIdDescriptionRequiredError
  | UpdateKnowledgeSourceDefinitionIdDescriptionTooLongError;

export type DeleteKnowledgeSourceDefinitionIdInProgressError = FetchUserEncodedError<
  SHARED_CONSTANTS_KNOWLEDGE_SOURCE["ERROR_CODES"]["DELETE_IN_PROGRESS"]
>;
export type DeleteKnowledgeSourceDefinitionIdInUseError = FetchUserEncodedError<
  SHARED_CONSTANTS_KNOWLEDGE_SOURCE["ERROR_CODES"]["DELETE_IN_USE"]
>;

export type DeleteKnowledgeSourceDefinitionIdErrors =
  | DeleteKnowledgeSourceDefinitionIdInProgressError
  | DeleteKnowledgeSourceDefinitionIdInUseError;

export const isDeleteKnowledgeSourceDefinitionIdError = (
  error: FetchRequestError
): error is DeleteKnowledgeSourceDefinitionIdErrors => {
  return error instanceof FetchUserEncodedError;
};

export type RefreshKnowledgeSourceDefinitionIdInProgressError = FetchUserEncodedError<
  SHARED_CONSTANTS_KNOWLEDGE_SOURCE["ERROR_CODES"]["REFRESH_IN_PROGRESS"]
>;
export type RefreshKnowledgeSourceDefinitionIdTooSoonError = FetchUserEncodedError<
  SHARED_CONSTANTS_KNOWLEDGE_SOURCE["ERROR_CODES"]["REFRESH_TOO_SOON"]
>;

export type RefreshKnowledgeSourceDefinitionIdErrors =
  | RefreshKnowledgeSourceDefinitionIdInProgressError
  | RefreshKnowledgeSourceDefinitionIdTooSoonError;

export const isRefreshKnowledgeSourceDefinitionIdError = (
  error: FetchRequestError
): error is RefreshKnowledgeSourceDefinitionIdErrors => {
  return error instanceof FetchUserEncodedError;
};

export const isUpdateKnowledgeSourceDefinitionIdError = (
  error: FetchRequestError
): error is UpdateKnowledgeSourceDefinitionIdErrors => {
  return error instanceof FetchUserEncodedError;
};

export type SearchKnowledgeSourceFilterOperator = "eq" | "in" | "gt" | "gte" | "lt" | "lte" | "near";

/** One structured filter, in the shape the knowledge source search tool accepts */
export interface SearchKnowledgeSourceFilter {
  field: string;
  operator: SearchKnowledgeSourceFilterOperator;
  value: string | null;
  array_value: string[] | null;
  near_value: { lat: number; lon: number; distance_km: number } | null;
}

export interface SearchKnowledgeSourceDefinitionIdProps {
  appGroupId: string;
  definitionId: string;
  query: string | null;
  filters: SearchKnowledgeSourceFilter[] | null;
}

export interface SearchKnowledgeSourceDefinitionIdResult {
  /** The search tool's output: a JSON string of results, or a model-safe error message */
  results: string;
}
